import api, { convertToISODateTime, handleApiError, fetchAllDataRest } from './client';
import { USER_ROLES } from '../../utils/constants';

export const userService = {
  getUsers: async (params = {}) => {
    try {
      const parsedAcademicYear = parseInt(params.academicYear, 10);
      const queryParams = {
        search: params.search || undefined,
        role: params.role || undefined,
        page: params.page || 1,
        limit: params.limit || 20,
        sortBy: params.sortBy || undefined,
        lastNameStartsWith: params.lastNameStartsWith || undefined,
        academicYear: Number.isNaN(parsedAcademicYear) ? undefined : parsedAcademicYear,
        consentFilter: params.consentFilter || undefined,
        archiveFilter: params.archiveFilter || 'active',
        certificateStatus: params.certificateStatus || undefined,
        membershipStatus: params.membershipStatus || undefined,
      };

      if (params.exportAll) {
        return await fetchAllDataRest('/api/users', queryParams, 'utenti');
      }

      const response = await api.get('/api/users', { params: queryParams });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get users error:', error);
      return handleApiError(error, 'Errore nel recupero degli utenti');
    }
  },

  getUser: async (id) => {
    try {
      const response = await api.get(`/api/users/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get user error:', error);
      return handleApiError(error, "Errore nel caricamento dell'utente");
    }
  },

  createUser: async (userData) => {
    try {
      const payload = {
        email: userData.email?.trim() || null,
        firstName: userData.firstName?.trim(),
        lastName: userData.lastName?.trim(),
        phoneNumber: userData.phoneNumber?.trim() || null,
        dateOfBirth: convertToISODateTime(userData.dateOfBirth),
        address: userData.address?.trim() || null,
        birthMunicipality: userData.birthMunicipality?.trim() || null,
        residenceMunicipality: userData.residenceMunicipality?.trim() || null,
        residenceProvince: userData.residenceProvince?.trim() || null,
        residencePostalCode: userData.residencePostalCode?.trim() || null,
        fiscalCode: userData.fiscalCode?.trim() || null,
        roles: userData.roles || [userData.role || USER_ROLES.ATHLETE],
      };

      const response = await api.post('/api/users', payload);
      return {
        success: true,
        data: response.data?.id || response.data,
        message: 'Utente creato con successo',
      };
    } catch (error) {
      console.error('Create user error:', error);
      return handleApiError(error, "Errore nella creazione dell'utente");
    }
  },

  updateUser: async (id, userData) => {
    try {
      const payload = {
        email: userData.email?.trim() || null,
        firstName: userData.firstName?.trim(),
        lastName: userData.lastName?.trim(),
        phoneNumber: userData.phoneNumber?.trim() || null,
        dateOfBirth: convertToISODateTime(userData.dateOfBirth),
        address: userData.address?.trim() || null,
        birthMunicipality: userData.birthMunicipality?.trim() || null,
        residenceMunicipality: userData.residenceMunicipality?.trim() || null,
        residenceProvince: userData.residenceProvince?.trim() || null,
        residencePostalCode: userData.residencePostalCode?.trim() || null,
        fiscalCode: userData.fiscalCode?.trim() || null,
        roles: userData.roles || [userData.role || USER_ROLES.MEMBER],
      };

      const response = await api.put(`/api/users/${id}`, payload);
      return {
        success: true,
        data: response.data,
        message: 'Utente aggiornato con successo',
      };
    } catch (error) {
      console.error('Update user error:', error);
      return handleApiError(error, "Errore nell'aggiornamento dell'utente");
    }
  },

  deleteUser: async (id) => {
    try {
      await api.delete(`/api/users/${id}`);
      return { success: true, message: 'Utente eliminato con successo' };
    } catch (error) {
      console.error('Delete user error:', error);
      return handleApiError(error, "Errore nell'eliminazione dell'utente");
    }
  },

  archiveUser: async (id, isArchived) => {
    try {
      const response = await api.post(`/api/users/${id}/archive`, { isArchived });
      return {
        success: true,
        data: response.data?.success ?? true,
        message: isArchived ? 'Atleta archiviato con successo' : 'Atleta ripristinato con successo',
      };
    } catch (error) {
      console.error('Archive user error:', error);
      return handleApiError(error, `Errore durante ${isArchived ? "l'archiviazione" : 'il ripristino'} dell'atleta`);
    }
  },

  getUserGroups: async (userId, academicYear, includeArchived = false, includeDeleted = false) => {
    try {
      const response = await api.get(`/api/groups/by-user/${userId}`, {
        params: {
          academicYear,
          includeArchived,
          includeDeleted,
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get user groups error:', error);
      return handleApiError(error, 'Errore nel caricamento dei gruppi');
    }
  },

  getUserAttendance: async (userId, groupId, month, year) => {
    try {
      const response = await api.get('/api/roll-calls/user-attendance', {
        params: { userId, groupId, month, year },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get user attendance error:', error);
      return handleApiError(error, 'Errore nel caricamento delle presenze');
    }
  },

  sendBroadcastEmail: async (recipientIds, subject, body, directEmailAddresses = null, attachments = []) => {
    try {
      if (attachments && attachments.length > 0) {
        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('body', body);
        (recipientIds || []).forEach(id => formData.append('recipientIds', id));
        (directEmailAddresses || []).forEach(email => formData.append('directEmailAddresses', email));
        attachments.forEach(file => formData.append('files', file));

        const response = await api.post('/api/users/broadcast-email', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return {
          success: response.data?.success ?? true,
          data: response.data,
        };
      }

      const payload = {
        recipientIds,
        subject,
        body,
        ...(directEmailAddresses && directEmailAddresses.length > 0 ? { directEmailAddresses } : {}),
      };
      const response = await api.post('/api/users/broadcast-email', payload);
      return {
        success: response.data?.success ?? true,
        data: response.data,
      };
    } catch (error) {
      console.error('Send broadcast email error:', error);
      return handleApiError(error, "Errore nell'invio delle email");
    }
  },

  downloadRegistrationForm: async (userId, fileName) => {
    try {
      const response = await api.get(`/api/File/user/${userId}/registration-form`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'modulo-iscrizione.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error) {
      console.error('Download registration form error:', error);
      return handleApiError(error, 'Errore durante il download del modulo di iscrizione');
    }
  },

  sendRegistrationForm: async (userId) => {
    try {
      const response = await api.post('/api/registrations/send-document-email', { userId });
      return {
        success: response.data?.success ?? true,
        data: response.data,
      };
    } catch (error) {
      console.error('Send registration form error:', error);
      return handleApiError(error, "Errore durante l'invio del modulo di iscrizione");
    }
  },
};

export default userService;
